import {
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    ChoiceList,
    Divider,
    FormLayout,
    InlineGrid,
    InlineStack,
    Layout,
    Modal,
    Page,
    Select,
    SkeletonBodyText,
    SkeletonPage,
    Tabs,
    Text,
    TextField,
    Thumbnail,
} from '@shopify/polaris';
import { DragHandleIcon } from '@shopify/polaris-icons';
import { observer } from 'mobx-react-lite';
import {
    Fragment,
    type PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import agent from '../../app/api/agent';
import { RealtimeProduct } from '../../app/models/Product';
import {
    createDefaultProductGuide,
    ProductGuideAnswer,
    ProductGuideAnswerMatchingValue,
    ProductGuideCollectionOption,
    ProductGuideDetail,
    ProductGuideDiscountType,
    ProductGuideItemType,
    ProductGuideQuestion,
    ProductGuideRecommendedPackage,
    ProductGuideRecommendedPackageItem,
    ProductGuideResultGroup,
    ProductGuideResultGroupFilterScope,
    ProductGuideResultProduct,
    ProductGuideTemplateKey,
    ProductGuideVehicleMode,
} from '../../app/models/ProductGuide';
import { ShopifyMetafieldAttributeOption, ShopifyMetafieldAttributeValueOption } from '../../app/models/ShopifyMetafieldAttribute';
import { useStore } from '../../app/stores/store';

type EditorSection = 'home' | 'questions' | 'items' | 'packages' | 'discounts' | 'preview';

type AnswerDraftState = {
    questionIndex: number;
    answerIndex: number | null;
    answer: ProductGuideAnswer;
};

type RecommendedPackageDraftState = {
    packageIndex: number | null;
    package: ProductGuideRecommendedPackage;
};

type RowDragState = {
    sourceIndex: number;
    destinationIndex: number;
    pointerX: number;
    pointerY: number;
    offsetX: number;
    offsetY: number;
    width: number;
    portalContainer: HTMLElement;
};

type QuestionDragState = RowDragState;
type PackageItemDragState = RowDragState;
type QuestionDragStartEvent = ReactPointerEvent<HTMLElement>;
type PackageItemDragStartEvent = ReactPointerEvent<HTMLElement>;

const guideTemplates: Array<{ value: ProductGuideTemplateKey; label: string; description: string }> = [
    {
        value: 'build-package',
        label: 'Build a package',
        description: 'Ask questions, let shoppers choose required package items, then review everything together.',
    },
    {
        value: 'recommend-one',
        label: 'Recommend one product',
        description: 'Ask questions and recommend a single best-fit product or variant.',
    },
    {
        value: 'find-compatible',
        label: 'Find compatible products',
        description: 'Guide shoppers to products compatible with a selected vehicle and answers.',
    },
    {
        value: 'custom',
        label: 'Blank guide',
        description: 'Start with a flexible guide and configure each section yourself.',
    },
];

const vehicleModeOptions: Array<{ value: ProductGuideVehicleMode; label: string }> = [
    { value: 'Required', label: 'Required' },
    { value: 'Optional', label: 'Optional' },
    { value: 'NotUsed', label: 'Not used' },
];

const packageItemTypeOptions: Array<{ value: ProductGuideItemType; label: string }> = [
    { value: 'FilteredProductChoice', label: 'Filtered product choice' },
    { value: 'FixedProduct', label: 'Fixed item' },
];

const discountTypeOptions: Array<{ value: ProductGuideDiscountType; label: string }> = [
    { value: 'Percentage', label: 'Percentage off' },
    { value: 'FixedAmount', label: 'Fixed amount off' },
];

const questionGridColumns = '32px minmax(240px, 1.3fr) minmax(220px, 360px) 92px 272px';
const packageItemGridColumns = '32px minmax(240px, 1.3fr) minmax(220px, 340px) 92px 244px';

const questionListStyle = {
    border: '1px solid var(--p-color-border)',
    borderRadius: 8,
    overflow: 'hidden',
} as const;

const questionHeaderGridStyle = {
    alignItems: 'center',
    background: '#f7f7f7',
    color: '#616161',
    display: 'grid',
    gap: 12,
    gridTemplateColumns: questionGridColumns,
    padding: '10px 16px',
} as const;

const questionRowStyle = {
    alignItems: 'center',
    background: 'white',
    borderTop: '1px solid var(--p-color-border-secondary)',
    display: 'grid',
    gap: 12,
    gridTemplateColumns: questionGridColumns,
    padding: '12px 16px',
} as const;

const questionAnswerPanelStyle = {
    background: 'var(--p-color-bg-surface-secondary)',
    borderTop: '1px solid var(--p-color-border-secondary)',
    padding: 16,
} as const;

const packageItemHeaderGridStyle = {
    ...questionHeaderGridStyle,
    gridTemplateColumns: packageItemGridColumns,
} as const;

const packageItemRowStyle = {
    ...questionRowStyle,
    gridTemplateColumns: packageItemGridColumns,
} as const;

const packageItemDetailPanelStyle = {
    ...questionAnswerPanelStyle,
} as const;

const quietStatusListStyle = {
    border: '1px solid var(--p-color-border-secondary)',
    borderRadius: 8,
    overflow: 'hidden',
} as const;

const quietStatusRowStyle = {
    alignItems: 'center',
    borderTop: '1px solid var(--p-color-border-secondary)',
    display: 'grid',
    gap: 12,
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    padding: '10px 12px',
} as const;

const dragHandleStyle = {
    alignItems: 'center',
    cursor: 'grab',
    display: 'inline-flex',
    height: 28,
    justifyContent: 'center',
    touchAction: 'none',
    userSelect: 'none',
    width: 28,
} as const;

const draggingRowStyle = {
    background: 'white',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.16)',
    opacity: 0.94,
    zIndex: 2147483647,
} as const;

const draggingSourceRowStyle = {
    opacity: 0.35,
} as const;

const toOrdered = <T extends { displayOrder: number }>(items: T[]) =>
    items.map((item, index) => ({ ...item, displayOrder: index + 1 }));

const remapMovedIndexKey = (key: string | undefined, sourceIndex: number, destinationIndex: number) => {
    if (key === undefined) return key;
    const index = Number(key);
    if (!Number.isFinite(index)) return key;
    if (index === sourceIndex) return String(destinationIndex);
    if (sourceIndex < destinationIndex && index > sourceIndex && index <= destinationIndex) return String(index - 1);
    if (sourceIndex > destinationIndex && index >= destinationIndex && index < sourceIndex) return String(index + 1);
    return key;
};

const remapRemovedIndexKey = (key: string | undefined, removedIndex: number) => {
    if (key === undefined) return key;
    const index = Number(key);
    if (!Number.isFinite(index)) return key;
    if (index === removedIndex) return undefined;
    if (index > removedIndex) return String(index - 1);
    return key;
};

const toKey = (value: string, fallback: string) => {
    const cleaned = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return cleaned || fallback;
};

const parsePositiveNumber = (value: string, fallback = 1) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
};

const parseNullablePositiveNumber = (value: string) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};

const parseNullableDecimal = (value: string) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const numberValue = (value?: number | null) => (value === undefined || value === null ? '' : String(value));

const collectionOptions = (collections: ProductGuideCollectionOption[], placeholder = 'No collection') => [
    { label: placeholder, value: '' },
    ...collections.map((collection) => ({
        label: collection.title,
        value: String(collection.id),
    })),
];

const connectProductAttributesOptionValue = '__connect_product_attributes__';
const unavailableCollectionFilterOptionValue = '__collection_filters_unavailable__';
const unavailableTagFilterOptionValue = '__tag_filters_unavailable__';

const filterByOptions = (attributes: ShopifyMetafieldAttributeOption[]) => [
    { label: 'No filter', value: '' },
    {
        title: 'Metafields',
        options: attributes.length > 0
            ? attributes.map((attribute) => ({
                label: attribute.name,
                value: String(attribute.id),
            }))
            : [
                {
                    label: 'Connect product attributes',
                    value: connectProductAttributesOptionValue,
                },
            ],
    },
    {
        title: 'Collections',
        options: [
            {
                label: 'No collection filters available',
                value: unavailableCollectionFilterOptionValue,
                disabled: true,
            },
        ],
    },
    {
        title: 'Tags',
        options: [
            {
                label: 'No tag filters available',
                value: unavailableTagFilterOptionValue,
                disabled: true,
            },
        ],
    },
];

const normalizeGuideForEditor = (guide: ProductGuideDetail): ProductGuideDetail => ({
    ...guide,
    templateKey: guide.templateKey || 'build-package',
    vehicleMode: guide.vehicleMode || (guide.requiresVehicleContext ? 'Required' : 'Optional'),
    discountType: guide.discountType || 'Percentage',
    questions: (guide.questions ?? []).map((question) => ({
        ...question,
        selectionMode: 'SingleChoice',
        connectedShopifyMetafieldAttributeId: question.connectedShopifyMetafieldAttributeId ?? null,
        connectedShopifyMetafieldAttributeName: question.connectedShopifyMetafieldAttributeName ?? '',
        connectedShopifyMetafieldAttributeOwnerType: question.connectedShopifyMetafieldAttributeOwnerType ?? '',
        connectedShopifyMetafieldAttributeNamespace: question.connectedShopifyMetafieldAttributeNamespace ?? '',
        connectedShopifyMetafieldAttributeKey: question.connectedShopifyMetafieldAttributeKey ?? '',
        answers: (question.answers ?? []).map((answer) => ({
            ...answer,
            imageUrl: answer.imageUrl ?? '',
            isEnabled: answer.isEnabled ?? true,
            matchingValues: answer.matchingValues ?? [],
            rules: answer.rules ?? [],
        })),
    })),
    resultGroups: (guide.resultGroups ?? []).map((group) => ({
        ...group,
        itemType: group.itemType || 'FilteredProductChoice',
        defaultQuantity: group.defaultQuantity || 1,
        minQuantity: group.minQuantity || 1,
        maxQuantity: group.maxQuantity || Math.max(1, group.defaultQuantity || 1),
        noResultsMessage: group.noResultsMessage ?? '',
        optionalAddOnCopy: group.optionalAddOnCopy ?? '',
        discountEligible: group.discountEligible ?? true,
        applyFilteringToFixedItem: group.applyFilteringToFixedItem ?? false,
        filterScopes: group.filterScopes ?? [],
        products: group.products ?? [],
    })),
    recommendedPackages: (guide.recommendedPackages ?? []).map((pkg) => ({
        ...pkg,
        imageUrl: pkg.imageUrl ?? '',
        isEnabled: pkg.isEnabled ?? true,
        discountEligible: pkg.discountEligible ?? true,
        items: pkg.items ?? [],
    })),
});

const createQuestion = (displayOrder: number): ProductGuideQuestion => ({
    prompt: '',
    helpText: '',
    selectionMode: 'SingleChoice',
    connectedShopifyMetafieldAttributeId: null,
    connectedShopifyMetafieldAttributeName: '',
    connectedShopifyMetafieldAttributeOwnerType: '',
    connectedShopifyMetafieldAttributeNamespace: '',
    connectedShopifyMetafieldAttributeKey: '',
    isRequired: true,
    displayOrder,
    answers: [],
});

const createAnswer = (displayOrder: number): ProductGuideAnswer => ({
    label: '',
    valueKey: '',
    helpText: '',
    imageUrl: '',
    isEnabled: true,
    isDefault: false,
    displayOrder,
    matchingValues: [],
    rules: [],
});

const createPackageItem = (displayOrder: number): ProductGuideResultGroup => ({
    title: '',
    slotKey: '',
    description: '',
    itemType: 'FilteredProductChoice',
    isRequired: true,
    minSelections: 1,
    maxSelections: 1,
    defaultQuantity: 1,
    minQuantity: 1,
    maxQuantity: 1,
    noResultsMessage: '',
    optionalAddOnCopy: '',
    discountEligible: true,
    applyFilteringToFixedItem: false,
    baseShopifyCollectionId: null,
    baseShopifyCollectionTitle: '',
    baseShopifyCollectionHandle: '',
    noResultsBehavior: 'ShowEmpty',
    fallbackShopifyCollectionId: null,
    fallbackShopifyCollectionTitle: '',
    fallbackShopifyCollectionHandle: '',
    displayOrder,
    filterScopes: [],
    products: [],
});

const createResultProduct = (product: RealtimeProduct, displayOrder: number): ProductGuideResultProduct => ({
    shopifyProductId: product.id,
    shopifyProductVariantId: null,
    productTitle: product.title,
    variantTitle: '',
    sku: product.sku,
    featuredImage: product.featuredImage,
    quantity: 1,
    isDefaultSelected: false,
    displayOrder,
});

const createRecommendedPackage = (displayOrder: number): ProductGuideRecommendedPackage => ({
    name: '',
    description: '',
    imageUrl: '',
    isEnabled: true,
    discountEligible: true,
    displayOrder,
    items: [],
});

const createRecommendedPackageItem = (displayOrder: number): ProductGuideRecommendedPackageItem => ({
    productGuideResultGroupId: 0,
    productGuideResultGroupTitle: '',
    shopifyProductId: 0,
    shopifyProductVariantId: null,
    productTitle: '',
    variantTitle: '',
    sku: '',
    featuredImage: '',
    quantity: 1,
    displayOrder,
});

const createMetafieldScope = (attribute: ShopifyMetafieldAttributeOption): ProductGuideResultGroupFilterScope => ({
    scopeType: 'ShopifyMetafieldAttribute',
    shopifyCollectionId: null,
    shopifyCollectionTitle: '',
    shopifyCollectionHandle: '',
    shopifyMetafieldAttributeId: attribute.id,
    shopifyMetafieldAttributeName: attribute.name,
    shopifyMetafieldAttributeOwnerType: attribute.ownerType,
    shopifyMetafieldAttributeNamespace: attribute.namespace,
    shopifyMetafieldAttributeKey: attribute.key,
    isActive: true,
});

const ProductGuideEditorPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { productGuideStore } = useStore();
    const {
        loadingInitial,
        loadingSave,
        selectedProductGuide,
        loadProductGuide,
        saveProductGuide,
        deleteProductGuide,
        clearSelectedProductGuide,
    } = productGuideStore;

    const guideId = useMemo(() => (id && id !== 'new' ? Number(id) : undefined), [id]);
    const [guide, setGuide] = useState<ProductGuideDetail | undefined>();
    const [activeSection, setActiveSection] = useState<EditorSection>('home');
    const [expandedQuestionKeys, setExpandedQuestionKeys] = useState<Set<string>>(new Set());
    const [expandedPackageItemKey, setExpandedPackageItemKey] = useState<string | undefined>();
    const [answerDraft, setAnswerDraft] = useState<AnswerDraftState | undefined>();
    const [recommendedPackageDraft, setRecommendedPackageDraft] = useState<RecommendedPackageDraftState | undefined>();
    const [collections, setCollections] = useState<ProductGuideCollectionOption[]>([]);
    const [metafieldAttributes, setMetafieldAttributes] = useState<ShopifyMetafieldAttributeOption[]>([]);
    const [setupDataError, setSetupDataError] = useState<string | undefined>();
    const [saveError, setSaveError] = useState<string | undefined>();
    const [productQuery, setProductQuery] = useState('');
    const [productResults, setProductResults] = useState<RealtimeProduct[]>([]);
    const [productSearchLoading, setProductSearchLoading] = useState(false);
    const questionRowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const questionDragRef = useRef<QuestionDragState | undefined>();
    const questionDragCleanupRef = useRef<(() => void) | undefined>();
    const [questionDrag, setQuestionDrag] = useState<QuestionDragState | undefined>();
    const packageItemRowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const packageItemDragRef = useRef<PackageItemDragState | undefined>();
    const packageItemDragCleanupRef = useRef<(() => void) | undefined>();
    const [packageItemDrag, setPackageItemDrag] = useState<PackageItemDragState | undefined>();

    useEffect(() => () => {
        questionDragCleanupRef.current?.();
        packageItemDragCleanupRef.current?.();
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadSetupData = async () => {
            try {
                const [collectionRows, attributeRows] = await Promise.all([
                    agent.ProductGuides.collections(),
                    agent.ShopifyMetafieldAttributes.list({ enabledOnly: true, includeValues: true }),
                ]);

                if (!cancelled) {
                    setCollections(collectionRows);
                    setMetafieldAttributes(attributeRows);
                }
            } catch (error) {
                if (!cancelled) {
                    setSetupDataError('Unable to load collections or product attributes for this guide.');
                }
            }
        };

        loadSetupData();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (guideId) {
            loadProductGuide(guideId);
        } else {
            clearSelectedProductGuide();
            setGuide(normalizeGuideForEditor(createDefaultProductGuide()));
        }

        return () => clearSelectedProductGuide();
    }, [clearSelectedProductGuide, guideId, loadProductGuide]);

    useEffect(() => {
        if (guideId && selectedProductGuide) {
            setGuide(normalizeGuideForEditor(selectedProductGuide));
        }
    }, [guideId, selectedProductGuide]);

    const template = useMemo(
        () => guideTemplates.find((item) => item.value === guide?.templateKey) ?? guideTemplates[0],
        [guide?.templateKey],
    );

    const validationMessages = useMemo(() => {
        if (!guide) return [];

        const messages: string[] = [];
        if (!guide.name.trim()) messages.push('Add a guide name.');
        if (!guide.handle.trim()) messages.push('Add a URL handle.');
        if (guide.questions.length === 0) messages.push('Add at least one shopper question.');
        if (guide.questions.some((question) => !question.prompt.trim())) messages.push('Finish all question prompts.');
        if (guide.questions.some((question) => question.answers.filter((answer) => answer.isEnabled).length === 0)) {
            messages.push('Each question needs at least one enabled answer.');
        }
        if (guide.resultGroups.length === 0 && guide.templateKey !== 'custom') {
            messages.push('Add at least one package item or product result.');
        }
        if (guide.resultGroups.some((group) => !group.title.trim())) {
            messages.push('Finish all package item names.');
        }
        if (guide.discountEnabled && guide.discountType === 'Percentage' && !guide.discountPercentage) {
            messages.push('Add a discount percentage or turn discounts off.');
        }
        if (guide.discountEnabled && guide.discountType === 'FixedAmount' && !guide.discountFixedAmount) {
            messages.push('Add a fixed discount amount or turn discounts off.');
        }

        return messages;
    }, [guide]);

    const connectedQuestionAttributes = useMemo(() => {
        if (!guide) return [];

        const ids = new Set(
            guide.questions
                .map((question) => question.connectedShopifyMetafieldAttributeId)
                .filter((attributeId): attributeId is number => !!attributeId),
        );

        return metafieldAttributes.filter((attribute) => ids.has(attribute.id));
    }, [guide, metafieldAttributes]);

    const patchGuide = useCallback((changes: Partial<ProductGuideDetail>) => {
        setGuide((current) => (current ? { ...current, ...changes } : current));
    }, []);

    const updateCollectionReference = useCallback((
        collectionId: string,
        onSelect: (collection: ProductGuideCollectionOption | undefined) => void,
    ) => {
        const collection = collections.find((item) => String(item.id) === collectionId);
        onSelect(collection);
    }, [collections]);

    const getQuestionAttribute = useCallback(
        (question: ProductGuideQuestion) =>
            metafieldAttributes.find((attribute) => attribute.id === question.connectedShopifyMetafieldAttributeId),
        [metafieldAttributes],
    );

    const patchQuestion = useCallback((questionIndex: number, changes: Partial<ProductGuideQuestion>) => {
        setGuide((current) => {
            if (!current) return current;
            return {
                ...current,
                questions: current.questions.map((question, index) => (index === questionIndex ? { ...question, ...changes } : question)),
            };
        });
    }, []);

    const connectQuestionAttribute = useCallback((questionIndex: number, value: string) => {
        if (value === connectProductAttributesOptionValue) {
            navigate('/storefront?section=attributes');
            return;
        }

        if (value === unavailableCollectionFilterOptionValue || value === unavailableTagFilterOptionValue) {
            return;
        }

        const selected = metafieldAttributes.find((item) => String(item.id) === value);
        patchQuestion(questionIndex, {
            connectedShopifyMetafieldAttributeId: selected?.id ?? null,
            connectedShopifyMetafieldAttributeName: selected?.name ?? '',
            connectedShopifyMetafieldAttributeOwnerType: selected?.ownerType ?? '',
            connectedShopifyMetafieldAttributeNamespace: selected?.namespace ?? '',
            connectedShopifyMetafieldAttributeKey: selected?.key ?? '',
        });
    }, [metafieldAttributes, navigate, patchQuestion]);

    const addQuestion = useCallback(() => {
        setGuide((current) => {
            if (!current) return current;
            const nextQuestions = toOrdered([...current.questions, createQuestion(current.questions.length + 1)]);
            return { ...current, questions: nextQuestions };
        });
    }, []);

    const removeQuestion = useCallback((questionIndex: number) => {
        setGuide((current) => {
            if (!current) return current;
            return {
                ...current,
                questions: toOrdered(current.questions.filter((_, index) => index !== questionIndex)),
            };
        });
    }, []);

    const moveQuestion = useCallback((sourceIndex: number, destinationIndex: number) => {
        setGuide((current) => {
            if (!current || sourceIndex === destinationIndex) return current;
            const questions = [...current.questions];
            const [movedQuestion] = questions.splice(sourceIndex, 1);
            questions.splice(destinationIndex, 0, movedQuestion);
            return { ...current, questions: toOrdered(questions) };
        });

        setExpandedQuestionKeys((current) => {
            if (current.size === 0) return current;
            const next = new Set<string>();
            current.forEach((key) => {
                const index = Number(key);
                if (!Number.isFinite(index)) return;
                if (index === sourceIndex) next.add(String(destinationIndex));
                else if (sourceIndex < destinationIndex && index > sourceIndex && index <= destinationIndex) next.add(String(index - 1));
                else if (sourceIndex > destinationIndex && index >= destinationIndex && index < sourceIndex) next.add(String(index + 1));
                else next.add(String(index));
            });
            return next;
        });
    }, []);

    const updateQuestionDrag = useCallback((pointerX: number, pointerY: number) => {
        const current = questionDragRef.current;
        if (!current || !guide) return;

        let destinationIndex = current.sourceIndex;
        const maxIndex = Math.max(guide.questions.length - 1, 0);
        const rowIndexes = guide.questions
            .map((_, index) => index)
            .filter((index) => index !== current.sourceIndex);

        destinationIndex = rowIndexes.length;
        rowIndexes.some((index, targetIndex) => {
            const node = questionRowRefs.current.get(index);
            if (!node) return false;
            const rect = node.getBoundingClientRect();
            if (pointerY < rect.top + rect.height / 2) {
                destinationIndex = targetIndex;
                return true;
            }
            return false;
        });

        destinationIndex = Math.max(0, Math.min(destinationIndex, maxIndex));

        if (destinationIndex !== current.sourceIndex) {
            moveQuestion(current.sourceIndex, destinationIndex);
        }

        const next = {
            ...current,
            sourceIndex: destinationIndex,
            destinationIndex,
            pointerX,
            pointerY,
        };
        questionDragRef.current = next;
        setQuestionDrag(next);
    }, [guide, moveQuestion]);

    const startQuestionDrag = useCallback((event: QuestionDragStartEvent, sourceIndex: number) => {
        const row = questionRowRefs.current.get(sourceIndex);
        if (!row) return;

        const rect = row.getBoundingClientRect();
        const ownerDocument = row.ownerDocument;
        const ownerWindow = ownerDocument.defaultView;
        const previousBodyUserSelect = ownerDocument.body.style.userSelect;

        const cleanup = () => {
            ownerDocument.removeEventListener('pointermove', handlePointerMove, true);
            ownerDocument.removeEventListener('pointerup', handlePointerUp, true);
            ownerDocument.removeEventListener('pointercancel', handlePointerCancel, true);
            ownerWindow?.removeEventListener('blur', handlePointerCancel, true);
            ownerDocument.body.style.userSelect = previousBodyUserSelect;
            questionDragCleanupRef.current = undefined;
        };

        const finishDrag = () => {
            cleanup();
            questionDragRef.current = undefined;
            setQuestionDrag(undefined);
        };

        const cancelDrag = () => {
            cleanup();
            questionDragRef.current = undefined;
            setQuestionDrag(undefined);
        };

        function handlePointerMove(moveEvent: PointerEvent) {
            moveEvent.preventDefault();
            updateQuestionDrag(moveEvent.clientX, moveEvent.clientY);
        }

        function handlePointerUp() {
            finishDrag();
        }

        function handlePointerCancel() {
            cancelDrag();
        }

        event.preventDefault();
        event.stopPropagation();

        ownerDocument.body.style.userSelect = 'none';
        ownerDocument.addEventListener('pointermove', handlePointerMove, true);
        ownerDocument.addEventListener('pointerup', handlePointerUp, true);
        ownerDocument.addEventListener('pointercancel', handlePointerCancel, true);
        ownerWindow?.addEventListener('blur', handlePointerCancel, true);

        const dragState = {
            sourceIndex,
            destinationIndex: sourceIndex,
            pointerX: event.clientX,
            pointerY: event.clientY,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width,
            portalContainer: ownerDocument.body,
        };

        questionDragCleanupRef.current = cleanup;
        questionDragRef.current = dragState;
        setQuestionDrag(dragState);
    }, [updateQuestionDrag]);

    const openAnswerModal = useCallback((questionIndex: number, answerIndex: number | null) => {
        setGuide((current) => {
            if (!current) return current;
            const answer = answerIndex === null
                ? createAnswer(current.questions[questionIndex].answers.length + 1)
                : current.questions[questionIndex].answers[answerIndex];
            setAnswerDraft({
                questionIndex,
                answerIndex,
                answer: { ...answer, matchingValues: [...(answer.matchingValues ?? [])], rules: [...(answer.rules ?? [])] },
            });
            return current;
        });
    }, []);

    const saveAnswerDraft = useCallback(() => {
        if (!answerDraft) return;

        setGuide((current) => {
            if (!current) return current;
            const questions = current.questions.map((question, index) => {
                if (index !== answerDraft.questionIndex) return question;
                const normalizedAnswer = {
                    ...answerDraft.answer,
                    label: answerDraft.answer.label.trim(),
                    valueKey: toKey(answerDraft.answer.valueKey || answerDraft.answer.label, `answer-${question.answers.length + 1}`),
                };
                const answers = answerDraft.answerIndex === null
                    ? [...question.answers, normalizedAnswer]
                    : question.answers.map((answer, answerIndex) => (answerIndex === answerDraft.answerIndex ? normalizedAnswer : answer));
                return { ...question, answers: toOrdered(answers) };
            });
            return { ...current, questions };
        });

        setAnswerDraft(undefined);
    }, [answerDraft]);

    const removeAnswer = useCallback((questionIndex: number, answerIndex: number) => {
        setGuide((current) => {
            if (!current) return current;
            return {
                ...current,
                questions: current.questions.map((question, index) => index === questionIndex
                    ? { ...question, answers: toOrdered(question.answers.filter((_, targetIndex) => targetIndex !== answerIndex)) }
                    : question),
            };
        });
    }, []);

    const generateAnswersFromAttribute = useCallback((questionIndex: number) => {
        setGuide((current) => {
            if (!current) return current;
            const question = current.questions[questionIndex];
            const attribute = getQuestionAttribute(question);
            if (!attribute || attribute.values.length === 0) return current;

            const existingNormalizedValues = new Set(
                question.answers
                    .flatMap((answer) => answer.matchingValues ?? [])
                    .map((value) => value.normalizedValue || value.value),
            );

            const generatedAnswers = attribute.values
                .filter((value) => !existingNormalizedValues.has(value.normalizedValue || value.value))
                .map((value, index) => ({
                    ...createAnswer(question.answers.length + index + 1),
                    label: value.value,
                    valueKey: toKey(value.value, `answer-${question.answers.length + index + 1}`),
                    matchingValues: [{ value: value.value, normalizedValue: value.normalizedValue }],
                }));

            return {
                ...current,
                questions: current.questions.map((item, index) => index === questionIndex
                    ? { ...item, answers: toOrdered([...item.answers, ...generatedAnswers]) }
                    : item),
            };
        });
    }, [getQuestionAttribute]);

    const addPackageItem = useCallback(() => {
        setGuide((current) => {
            if (!current) return current;
            const nextIndex = current.resultGroups.length;
            const group = {
                ...createPackageItem(nextIndex + 1),
                title: `Item ${nextIndex + 1}`,
                slotKey: `item-${nextIndex + 1}`,
            };
            setExpandedPackageItemKey(String(nextIndex));
            setProductQuery('');
            setProductResults([]);
            return { ...current, resultGroups: toOrdered([...current.resultGroups, group]) };
        });
    }, []);

    const patchPackageItem = useCallback((groupIndex: number, changes: Partial<ProductGuideResultGroup>) => {
        setGuide((current) => {
            if (!current) return current;
            return {
                ...current,
                resultGroups: current.resultGroups.map((group, index) =>
                    index === groupIndex ? { ...group, ...changes } : group,
                ),
            };
        });
    }, []);

    const removePackageItem = useCallback((groupIndex: number) => {
        setGuide((current) => {
            if (!current) return current;
            const removedGroupId = current.resultGroups[groupIndex]?.id;
            const resultGroups = toOrdered(current.resultGroups.filter((_, index) => index !== groupIndex));
            const recommendedPackages = removedGroupId
                ? current.recommendedPackages.map((pkg) => ({
                    ...pkg,
                    items: toOrdered(pkg.items.filter((item) => item.productGuideResultGroupId !== removedGroupId)),
                }))
                : current.recommendedPackages;

            return { ...current, resultGroups, recommendedPackages };
        });

        setExpandedPackageItemKey((current) => remapRemovedIndexKey(current, groupIndex));
        setProductQuery('');
        setProductResults([]);
    }, []);

    const movePackageItem = useCallback((sourceIndex: number, destinationIndex: number) => {
        setGuide((current) => {
            if (!current || sourceIndex === destinationIndex) return current;
            const resultGroups = [...current.resultGroups];
            const [movedGroup] = resultGroups.splice(sourceIndex, 1);
            resultGroups.splice(destinationIndex, 0, movedGroup);
            return { ...current, resultGroups: toOrdered(resultGroups) };
        });

        setExpandedPackageItemKey((current) => remapMovedIndexKey(current, sourceIndex, destinationIndex));
    }, []);

    const updatePackageItemDrag = useCallback((pointerX: number, pointerY: number) => {
        const current = packageItemDragRef.current;
        if (!current || !guide) return;

        let destinationIndex = current.sourceIndex;
        const maxIndex = Math.max(guide.resultGroups.length - 1, 0);
        const rowIndexes = guide.resultGroups
            .map((_, index) => index)
            .filter((index) => index !== current.sourceIndex);

        destinationIndex = rowIndexes.length;
        rowIndexes.some((index, targetIndex) => {
            const node = packageItemRowRefs.current.get(index);
            if (!node) return false;
            const rect = node.getBoundingClientRect();
            if (pointerY < rect.top + rect.height / 2) {
                destinationIndex = targetIndex;
                return true;
            }
            return false;
        });

        destinationIndex = Math.max(0, Math.min(destinationIndex, maxIndex));

        if (destinationIndex !== current.sourceIndex) {
            movePackageItem(current.sourceIndex, destinationIndex);
        }

        const next = {
            ...current,
            sourceIndex: destinationIndex,
            destinationIndex,
            pointerX,
            pointerY,
        };
        packageItemDragRef.current = next;
        setPackageItemDrag(next);
    }, [guide, movePackageItem]);

    const startPackageItemDrag = useCallback((event: PackageItemDragStartEvent, sourceIndex: number) => {
        const row = packageItemRowRefs.current.get(sourceIndex);
        if (!row) return;

        const rect = row.getBoundingClientRect();
        const ownerDocument = row.ownerDocument;
        const ownerWindow = ownerDocument.defaultView;
        const previousBodyUserSelect = ownerDocument.body.style.userSelect;

        const cleanup = () => {
            ownerDocument.removeEventListener('pointermove', handlePointerMove, true);
            ownerDocument.removeEventListener('pointerup', handlePointerUp, true);
            ownerDocument.removeEventListener('pointercancel', handlePointerCancel, true);
            ownerWindow?.removeEventListener('blur', handlePointerCancel, true);
            ownerDocument.body.style.userSelect = previousBodyUserSelect;
            packageItemDragCleanupRef.current = undefined;
        };

        const finishDrag = () => {
            cleanup();
            packageItemDragRef.current = undefined;
            setPackageItemDrag(undefined);
        };

        const cancelDrag = () => {
            cleanup();
            packageItemDragRef.current = undefined;
            setPackageItemDrag(undefined);
        };

        function handlePointerMove(moveEvent: PointerEvent) {
            moveEvent.preventDefault();
            updatePackageItemDrag(moveEvent.clientX, moveEvent.clientY);
        }

        function handlePointerUp() {
            finishDrag();
        }

        function handlePointerCancel() {
            cancelDrag();
        }

        event.preventDefault();
        event.stopPropagation();

        ownerDocument.body.style.userSelect = 'none';
        ownerDocument.addEventListener('pointermove', handlePointerMove, true);
        ownerDocument.addEventListener('pointerup', handlePointerUp, true);
        ownerDocument.addEventListener('pointercancel', handlePointerCancel, true);
        ownerWindow?.addEventListener('blur', handlePointerCancel, true);

        const dragState = {
            sourceIndex,
            destinationIndex: sourceIndex,
            pointerX: event.clientX,
            pointerY: event.clientY,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width,
            portalContainer: ownerDocument.body,
        };

        packageItemDragCleanupRef.current = cleanup;
        packageItemDragRef.current = dragState;
        setPackageItemDrag(dragState);
    }, [updatePackageItemDrag]);

    const togglePackageItemDetails = useCallback((groupIndex: number) => {
        setExpandedPackageItemKey((current) => (current === String(groupIndex) ? undefined : String(groupIndex)));
        setProductQuery('');
        setProductResults([]);
    }, []);

    const updatePackageItemAttributeScopes = useCallback((groupIndex: number, selected: string[]) => {
        const selectedIds = new Set(selected.map(Number));
        const scopes = connectedQuestionAttributes
            .filter((attribute) => selectedIds.has(attribute.id))
            .map(createMetafieldScope);
        patchPackageItem(groupIndex, { filterScopes: scopes });
    }, [connectedQuestionAttributes, patchPackageItem]);

    const removeProductFromPackageItem = useCallback((groupIndex: number, productIndex: number) => {
        setGuide((current) => {
            if (!current) return current;
            return {
                ...current,
                resultGroups: current.resultGroups.map((group, index) => index === groupIndex
                    ? { ...group, products: toOrdered(group.products.filter((_, targetIndex) => targetIndex !== productIndex)) }
                    : group),
            };
        });
    }, []);

    const searchProducts = useCallback(async () => {
        if (!productQuery.trim()) {
            setProductResults([]);
            return;
        }

        setProductSearchLoading(true);
        try {
            const result = await agent.Products.listRealtime(productQuery.trim(), '', 20, 'title asc');
            setProductResults(result.products);
        } finally {
            setProductSearchLoading(false);
        }
    }, [productQuery]);

    const addProductToPackageItem = useCallback((groupIndex: number, product: RealtimeProduct) => {
        setGuide((current) => {
            if (!current) return current;
            return {
                ...current,
                resultGroups: current.resultGroups.map((group, index) => {
                    if (index !== groupIndex) return group;
                    if (group.products.some((item) => item.shopifyProductId === product.id && !item.shopifyProductVariantId)) {
                        return group;
                    }

                    return {
                        ...group,
                        products: toOrdered([...group.products, createResultProduct(product, group.products.length + 1)]),
                    };
                }),
            };
        });
    }, []);

    const openRecommendedPackageModal = useCallback((packageIndex: number | null) => {
        setGuide((current) => {
            if (!current) return current;
            const pkg = packageIndex === null
                ? createRecommendedPackage(current.recommendedPackages.length + 1)
                : current.recommendedPackages[packageIndex];
            setRecommendedPackageDraft({
                packageIndex,
                package: { ...pkg, items: [...(pkg.items ?? [])] },
            });
            return current;
        });
    }, []);

    const saveRecommendedPackageDraft = useCallback(() => {
        if (!recommendedPackageDraft) return;

        setGuide((current) => {
            if (!current) return current;
            const savedGroupIds = new Set(current.resultGroups.map((group) => group.id).filter((groupId): groupId is number => !!groupId));
            const normalizedPackage = {
                ...recommendedPackageDraft.package,
                name: recommendedPackageDraft.package.name.trim(),
                items: toOrdered(recommendedPackageDraft.package.items.filter((item) =>
                    savedGroupIds.has(item.productGuideResultGroupId)
                    && item.shopifyProductId > 0,
                )),
            };
            const packages = recommendedPackageDraft.packageIndex === null
                ? [...current.recommendedPackages, normalizedPackage]
                : current.recommendedPackages.map((pkg, index) => (index === recommendedPackageDraft.packageIndex ? normalizedPackage : pkg));

            return { ...current, recommendedPackages: toOrdered(packages) };
        });

        setRecommendedPackageDraft(undefined);
    }, [recommendedPackageDraft]);

    const removeRecommendedPackage = useCallback((packageIndex: number) => {
        setGuide((current) => {
            if (!current) return current;
            return {
                ...current,
                recommendedPackages: toOrdered(current.recommendedPackages.filter((_, index) => index !== packageIndex)),
            };
        });
    }, []);

    const handleSave = useCallback(async () => {
        if (!guide) return;
        setSaveError(undefined);

        if (guide.isActive && validationMessages.length > 0) {
            setSaveError('The guide cannot be activated until the setup checklist is complete.');
            return;
        }

        try {
            const saved = await saveProductGuide(guide);
            setGuide(normalizeGuideForEditor(saved));
            if (!guideId && saved.id) {
                navigate(`/product-guides/${saved.id}`, { replace: true });
            }
        } catch (error) {
            setSaveError('Unable to save this product guide.');
        }
    }, [guide, guideId, navigate, saveProductGuide, validationMessages.length]);

    const handleDelete = useCallback(async () => {
        if (!guide?.id) return;
        await deleteProductGuide(guide.id);
        navigate('/product-guides');
    }, [deleteProductGuide, guide?.id, navigate]);

    const renderChecklist = () => {
        if (!guide) return null;
        const rows = [
            { label: 'Guide details', complete: !!guide.name.trim() && !!guide.handle.trim() },
            { label: 'Questions', complete: guide.questions.length > 0 && guide.questions.every((question) => question.prompt.trim() && question.answers.some((answer) => answer.isEnabled)) },
            { label: 'Package items', complete: guide.templateKey === 'custom' || guide.resultGroups.length > 0 },
            { label: 'Discount settings', complete: !guide.discountEnabled || (guide.discountType === 'Percentage' ? !!guide.discountPercentage : !!guide.discountFixedAmount) },
        ];

        return (
            <Card>
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="start">
                        <BlockStack gap="050">
                            <Text as="h2" variant="headingMd">Guide setup</Text>
                            <Text as="p" tone="subdued">Review what needs to be finished before the guide can run on the storefront.</Text>
                        </BlockStack>
                        <Badge tone={validationMessages.length === 0 ? 'success' : 'attention'}>
                            {validationMessages.length === 0 ? 'Ready' : `${validationMessages.length} remaining`}
                        </Badge>
                    </InlineStack>
                    <div style={quietStatusListStyle}>
                        {rows.map((row, index) => (
                            <div
                                key={row.label}
                                style={{
                                    ...quietStatusRowStyle,
                                    borderTop: index === 0 ? 'none' : quietStatusRowStyle.borderTop,
                                }}
                            >
                                <Text as="span">{row.label}</Text>
                                <Text as="span" tone="subdued">{row.complete ? 'Complete' : 'Needs setup'}</Text>
                            </div>
                        ))}
                    </div>
                    {validationMessages.length > 0 && (
                        <Banner tone="warning">
                            <BlockStack gap="100">
                                {validationMessages.map((message) => (
                                    <Text as="p" key={message}>{message}</Text>
                                ))}
                            </BlockStack>
                        </Banner>
                    )}
                </BlockStack>
            </Card>
        );
    };

    const renderSectionTabs = () => {
        const questionCount = guide?.questions.length ?? 0;
        const itemCount = guide?.resultGroups.length ?? 0;
        const sections: Array<{ key: EditorSection; label: string; badge?: string }> = [
            { key: 'home', label: 'Home' },
            { key: 'questions', label: 'Questions', badge: questionCount > 0 ? String(questionCount) : undefined },
            {
                key: 'items',
                label: guide?.templateKey === 'recommend-one' ? 'Product' : guide?.templateKey === 'find-compatible' ? 'Results' : 'Items',
                badge: itemCount > 0 ? String(itemCount) : undefined,
            },
            { key: 'packages', label: 'Packages' },
            { key: 'discounts', label: 'Cart' },
            { key: 'preview', label: 'Preview' },
        ];
        const selected = Math.max(0, sections.findIndex((section) => section.key === activeSection));

        return (
            <Tabs
                tabs={sections.map((section) => ({
                    id: `product-guide-${section.key}`,
                    content: section.label,
                    badge: section.badge,
                    panelID: `product-guide-${section.key}-panel`,
                }))}
                selected={selected}
                onSelect={(selectedIndex) => setActiveSection(sections[selectedIndex].key)}
            />
        );
    };

    const renderGuideDetails = () => {
        if (!guide) return null;

        return (
            <Card>
                <BlockStack gap="400">
                    <BlockStack gap="100">
                        <Text as="h2" variant="headingMd">Guide details</Text>
                        <Text as="p" tone="subdued">Choose a starting template and define the storefront-facing guide details.</Text>
                    </BlockStack>
                    <BlockStack gap="300">
                        <Select
                            label="Template"
                            options={guideTemplates.map((item) => ({ label: item.label, value: item.value }))}
                            value={guide.templateKey}
                            onChange={(value) => patchGuide({ templateKey: value })}
                        />
                        <Text as="p" tone="subdued">{template.description}</Text>
                    </BlockStack>
                    <Divider />
                    <FormLayout>
                        <TextField
                            label="Name"
                            value={guide.name}
                            onChange={(value) => patchGuide({ name: value, handle: guide.handle || toKey(value, 'guide') })}
                            autoComplete="off"
                        />
                        <TextField
                            label="Handle"
                            value={guide.handle}
                            onChange={(value) => patchGuide({ handle: toKey(value, 'guide') })}
                            autoComplete="off"
                        />
                        <TextField
                            label="Description"
                            value={guide.description}
                            onChange={(value) => patchGuide({ description: value })}
                            multiline={3}
                            autoComplete="off"
                        />
                    </FormLayout>
                    <Divider />
                    <FormLayout>
                        <Select
                            label="Vehicle selection"
                            options={vehicleModeOptions}
                            value={guide.vehicleMode}
                            onChange={(value) => patchGuide({
                                vehicleMode: value,
                                requiresVehicleContext: value === 'Required',
                            })}
                        />
                        <Checkbox
                            label="Active"
                            helpText="Active guides can render on the storefront after the setup checklist is complete."
                            checked={guide.isActive}
                            onChange={(checked) => patchGuide({ isActive: checked })}
                        />
                    </FormLayout>
                </BlockStack>
            </Card>
        );
    };

    const renderGuideHome = () => (
        <BlockStack gap="400">
            {renderChecklist()}
            {renderGuideDetails()}
        </BlockStack>
    );

    const renderQuestions = () => {
        if (!guide) return null;

        const draggedQuestion = questionDrag ? guide.questions[questionDrag.sourceIndex] : undefined;

        const renderQuestionRow = (
            question: ProductGuideQuestion,
            questionIndex: number,
            options: {
                isDragging?: boolean;
                isPreview?: boolean;
                onHandleDragStart?: (event: QuestionDragStartEvent) => void;
                setRef?: (node: HTMLDivElement | null) => void;
            } = {},
        ) => {
            const enabledAnswerCount = question.answers.filter((answer) => answer.isEnabled).length;
            const questionLabel = question.prompt.trim() || `Question ${questionIndex + 1}`;

            return (
                <div
                    ref={options.setRef}
                    style={{
                        ...questionRowStyle,
                        ...(options.isDragging ? draggingSourceRowStyle : {}),
                        ...(options.isPreview ? draggingRowStyle : {}),
                    }}
                >
                    <InlineStack blockAlign="center">
                        <span
                            aria-label={`Drag ${questionLabel} to reorder`}
                            onPointerDown={options.onHandleDragStart}
                            role="button"
                            style={{
                                ...dragHandleStyle,
                                cursor: options.isDragging || options.isPreview ? 'grabbing' : 'grab',
                            }}
                            tabIndex={0}
                            title="Drag to reorder"
                        >
                            <DragHandleIcon width={20} height={20} />
                        </span>
                    </InlineStack>

                    <TextField
                        label="Question"
                        labelHidden
                        value={question.prompt}
                        placeholder={`Question ${questionIndex + 1}`}
                        onChange={(value) => patchQuestion(questionIndex, { prompt: value })}
                        autoComplete="off"
                    />

                    <Select
                        label="Filter by"
                        labelHidden
                        options={filterByOptions(metafieldAttributes)}
                        value={String(question.connectedShopifyMetafieldAttributeId ?? '')}
                        onChange={(value) => connectQuestionAttribute(questionIndex, value)}
                    />

                    <Checkbox
                        label="Required"
                        labelHidden
                        checked={question.isRequired}
                        onChange={(checked) => patchQuestion(questionIndex, { isRequired: checked })}
                    />

                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                        <Button onClick={() => setExpandedQuestionKeys((current) => {
                            const next = new Set(current);
                            if (next.has(String(questionIndex))) next.delete(String(questionIndex));
                            else next.add(String(questionIndex));
                            return next;
                        })}>
                            {expandedQuestionKeys.has(String(questionIndex))
                                ? `Hide answers (${enabledAnswerCount}/${question.answers.length})`
                                : `Answers (${enabledAnswerCount}/${question.answers.length})`}
                        </Button>
                        <Button tone="critical" onClick={() => removeQuestion(questionIndex)}>Remove</Button>
                    </InlineStack>
                </div>
            );
        };

        const renderQuestionAnswersPanel = (question: ProductGuideQuestion, questionIndex: number) => {
            const attribute = getQuestionAttribute(question);

            return (
                <div style={questionAnswerPanelStyle}>
                    <BlockStack gap="400">
                        <TextField
                            label="Help text"
                            value={question.helpText}
                            onChange={(value) => patchQuestion(questionIndex, { helpText: value })}
                            multiline={2}
                            autoComplete="off"
                        />
                        <InlineStack align="space-between" blockAlign="center">
                            <BlockStack gap="050">
                                <Text as="h4" variant="headingSm">Answers</Text>
                                <Text as="p" tone="subdued">Generated answers are editable and will not auto-change when Shopify values change later.</Text>
                            </BlockStack>
                            <InlineStack gap="200">
                                <Button
                                    disabled={!attribute || attribute.values.length === 0}
                                    onClick={() => generateAnswersFromAttribute(questionIndex)}
                                >
                                    Generate from values
                                </Button>
                                <Button onClick={() => openAnswerModal(questionIndex, null)}>Add answer</Button>
                            </InlineStack>
                        </InlineStack>

                        {question.answers.length === 0 ? (
                            <Banner tone="info">Add answers or generate them from the selected filter values.</Banner>
                        ) : (
                            <BlockStack gap="200">
                                {question.answers.map((answer, answerIndex) => (
                                    <Box key={`${answer.id ?? 'answer'}-${answerIndex}`} padding="300" background="bg-surface" borderRadius="200">
                                        <InlineStack align="space-between" blockAlign="center">
                                            <BlockStack gap="050">
                                                <InlineStack gap="200" blockAlign="center">
                                                    <Text as="span" fontWeight="semibold">{answer.label || 'Untitled answer'}</Text>
                                                    {!answer.isEnabled && <Badge tone="warning">Disabled</Badge>}
                                                    {answer.isDefault && <Badge tone="info">Default</Badge>}
                                                </InlineStack>
                                                <Text as="p" tone="subdued">
                                                    {answer.matchingValues.length > 0
                                                        ? `${answer.matchingValues.length} matching value${answer.matchingValues.length === 1 ? '' : 's'}`
                                                        : 'No matching values selected'}
                                                </Text>
                                            </BlockStack>
                                            <InlineStack gap="200">
                                                <Button onClick={() => openAnswerModal(questionIndex, answerIndex)}>Edit</Button>
                                                <Button tone="critical" onClick={() => removeAnswer(questionIndex, answerIndex)}>Remove</Button>
                                            </InlineStack>
                                        </InlineStack>
                                    </Box>
                                ))}
                            </BlockStack>
                        )}
                    </BlockStack>
                </div>
            );
        };

        return (
            <Card>
                <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="start">
                        <BlockStack gap="100">
                            <Text as="h2" variant="headingMd">Questions</Text>
                            <Text as="p" tone="subdued">Choose what each question filters by, then map answers to cached attribute values.</Text>
                        </BlockStack>
                        <Button onClick={addQuestion}>Add question</Button>
                    </InlineStack>

                    {guide.questions.length === 0 && (
                        <Banner tone="info">Add the first question shoppers answer before product results are shown.</Banner>
                    )}

                    {guide.questions.length > 0 && (
                        <div style={questionListStyle}>
                            <div style={questionHeaderGridStyle}>
                                <div />
                                <Text as="span" variant="bodySm" fontWeight="medium">Question</Text>
                                <Text as="span" variant="bodySm" fontWeight="medium">Filter by</Text>
                                <Text as="span" variant="bodySm" fontWeight="medium">Required</Text>
                                <Text as="span" variant="bodySm" fontWeight="medium">Actions</Text>
                            </div>

                            {guide.questions.map((question, questionIndex) => (
                                <Fragment key={`${question.id ?? 'question'}-${questionIndex}`}>
                                    {renderQuestionRow(question, questionIndex, {
                                        isDragging: questionDrag?.sourceIndex === questionIndex,
                                        onHandleDragStart: questionDrag ? undefined : (event) => startQuestionDrag(event, questionIndex),
                                        setRef: (node) => {
                                            if (node) {
                                                questionRowRefs.current.set(questionIndex, node);
                                            } else {
                                                questionRowRefs.current.delete(questionIndex);
                                            }
                                        },
                                    })}
                                    {expandedQuestionKeys.has(String(questionIndex)) && renderQuestionAnswersPanel(question, questionIndex)}
                                </Fragment>
                            ))}
                        </div>
                    )}

                    {questionDrag && draggedQuestion ? createPortal((
                        <div
                            style={{
                                left: questionDrag.pointerX - questionDrag.offsetX,
                                pointerEvents: 'none',
                                position: 'fixed',
                                top: questionDrag.pointerY - questionDrag.offsetY,
                                width: questionDrag.width,
                                zIndex: 2147483647,
                            }}
                        >
                            {renderQuestionRow(draggedQuestion, questionDrag.sourceIndex, { isPreview: true })}
                        </div>
                    ), questionDrag.portalContainer) : null}

                </BlockStack>
            </Card>
        );
    };

    const renderPackageItems = () => {
        if (!guide) return null;
        const title = guide.templateKey === 'recommend-one'
            ? 'Recommended product'
            : guide.templateKey === 'find-compatible'
                ? 'Product results'
                : 'Package items';
        const draggedPackageItem = packageItemDrag ? guide.resultGroups[packageItemDrag.sourceIndex] : undefined;

        const renderPackageItemRow = (
            group: ProductGuideResultGroup,
            groupIndex: number,
            options: {
                isDragging?: boolean;
                isPreview?: boolean;
                onHandleDragStart?: (event: PackageItemDragStartEvent) => void;
                setRef?: (node: HTMLDivElement | null) => void;
            } = {},
        ) => {
            const itemLabel = group.title.trim() || `Item ${groupIndex + 1}`;

            return (
                <div
                    ref={options.setRef}
                    style={{
                        ...packageItemRowStyle,
                        ...(options.isDragging ? draggingSourceRowStyle : {}),
                        ...(options.isPreview ? draggingRowStyle : {}),
                    }}
                >
                    <InlineStack blockAlign="center">
                        <span
                            aria-label={`Drag ${itemLabel} to reorder`}
                            onPointerDown={options.onHandleDragStart}
                            role="button"
                            style={{
                                ...dragHandleStyle,
                                cursor: options.isDragging || options.isPreview ? 'grabbing' : 'grab',
                            }}
                            tabIndex={0}
                            title="Drag to reorder"
                        >
                            <DragHandleIcon width={20} height={20} />
                        </span>
                    </InlineStack>

                    <TextField
                        label="Item"
                        labelHidden
                        value={group.title}
                        placeholder={`Item ${groupIndex + 1}`}
                        onChange={(value) => patchPackageItem(groupIndex, {
                            title: value,
                            slotKey: group.slotKey || toKey(value, `item-${groupIndex + 1}`),
                        })}
                        autoComplete="off"
                    />

                    <Select
                        label="Behavior"
                        labelHidden
                        options={packageItemTypeOptions}
                        value={group.itemType}
                        onChange={(value) => patchPackageItem(groupIndex, { itemType: value as ProductGuideItemType })}
                    />

                    <Checkbox
                        label="Required"
                        labelHidden
                        checked={group.isRequired}
                        onChange={(checked) => patchPackageItem(groupIndex, { isRequired: checked })}
                    />

                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                        <Button onClick={() => togglePackageItemDetails(groupIndex)}>
                            {expandedPackageItemKey === String(groupIndex) ? 'Hide details' : 'Details'}
                        </Button>
                        <Button tone="critical" onClick={() => removePackageItem(groupIndex)}>Remove</Button>
                    </InlineStack>
                </div>
            );
        };

        const renderPackageItemDetails = (group: ProductGuideResultGroup, groupIndex: number) => {
            const selectedAttributeScopeIds = group.filterScopes
                .filter((scope) => scope.scopeType === 'ShopifyMetafieldAttribute' && scope.shopifyMetafieldAttributeId)
                .map((scope) => String(scope.shopifyMetafieldAttributeId));

            const updateGroup = (changes: Partial<ProductGuideResultGroup>) => {
                patchPackageItem(groupIndex, changes);
            };

            return (
                <div style={packageItemDetailPanelStyle}>
                    <BlockStack gap="400">
                        <BlockStack gap="300">
                            <Text as="h3" variant="headingSm">Item details</Text>
                            <FormLayout>
                                <TextField
                                    label="Key"
                                    value={group.slotKey}
                                    onChange={(value) => updateGroup({ slotKey: toKey(value, `item-${groupIndex + 1}`) })}
                                    autoComplete="off"
                                />
                                <TextField
                                    label="Description"
                                    value={group.description}
                                    onChange={(value) => updateGroup({ description: value })}
                                    multiline={2}
                                    autoComplete="off"
                                />
                                {!group.isRequired && (
                                    <TextField
                                        label="Optional add-on copy"
                                        value={group.optionalAddOnCopy}
                                        onChange={(value) => updateGroup({ optionalAddOnCopy: value })}
                                        autoComplete="off"
                                    />
                                )}
                            </FormLayout>
                        </BlockStack>

                        <Divider />

                        <BlockStack gap="300">
                            <Text as="h3" variant="headingSm">Product source</Text>
                            <FormLayout>
                                <Select
                                    label="Base collection"
                                    options={collectionOptions(collections, 'Use all eligible products')}
                                    value={String(group.baseShopifyCollectionId ?? '')}
                                    onChange={(value) => updateCollectionReference(value, (collection) => updateGroup({
                                        baseShopifyCollectionId: collection?.id ?? null,
                                        baseShopifyCollectionTitle: collection?.title ?? '',
                                        baseShopifyCollectionHandle: collection?.handle ?? '',
                                    }))}
                                />
                                <TextField
                                    label="No products message"
                                    value={group.noResultsMessage}
                                    onChange={(value) => updateGroup({ noResultsMessage: value })}
                                    multiline={2}
                                    autoComplete="off"
                                />
                            </FormLayout>
                        </BlockStack>

                        <Divider />

                        <BlockStack gap="300">
                            <Text as="h3" variant="headingSm">Quantity and cart behavior</Text>
                            <FormLayout>
                                <TextField
                                    label="Default quantity"
                                    type="number"
                                    value={numberValue(group.defaultQuantity)}
                                    onChange={(value) => updateGroup({ defaultQuantity: parsePositiveNumber(value) })}
                                    autoComplete="off"
                                />
                                <TextField
                                    label="Minimum quantity"
                                    type="number"
                                    value={numberValue(group.minQuantity)}
                                    onChange={(value) => updateGroup({ minQuantity: parsePositiveNumber(value) })}
                                    autoComplete="off"
                                />
                                <TextField
                                    label="Maximum quantity"
                                    type="number"
                                    value={numberValue(group.maxQuantity)}
                                    onChange={(value) => updateGroup({ maxQuantity: parsePositiveNumber(value) })}
                                    autoComplete="off"
                                />
                                <Checkbox
                                    label="Eligible for bundle discount"
                                    checked={group.discountEligible}
                                    onChange={(checked) => updateGroup({ discountEligible: checked })}
                                />
                                <Checkbox
                                    label="Apply question filters to fixed item"
                                    checked={group.applyFilteringToFixedItem}
                                    disabled={group.itemType !== 'FixedProduct'}
                                    onChange={(checked) => updateGroup({ applyFilteringToFixedItem: checked })}
                                />
                            </FormLayout>
                        </BlockStack>

                        <Divider />

                        <BlockStack gap="200">
                            <Text as="h3" variant="headingSm">Question filters applied to this item</Text>
                            {connectedQuestionAttributes.length > 0 ? (
                                <ChoiceList
                                    title="Attributes"
                                    allowMultiple
                                    choices={connectedQuestionAttributes.map((attribute) => ({
                                        label: attribute.name,
                                        value: String(attribute.id),
                                    }))}
                                    selected={selectedAttributeScopeIds}
                                    onChange={(selected) => updatePackageItemAttributeScopes(groupIndex, selected)}
                                />
                            ) : (
                                <Banner tone="info">Connect questions to attributes before selecting item filters.</Banner>
                            )}
                        </BlockStack>

                        <Divider />

                        <BlockStack gap="300">
                            <Text as="h3" variant="headingSm">Manual products and fixed items</Text>
                            <InlineStack gap="200" blockAlign="end">
                                <Box minWidth="320px">
                                    <TextField
                                        label="Search Shopify products"
                                        value={productQuery}
                                        onChange={setProductQuery}
                                        autoComplete="off"
                                    />
                                </Box>
                                <Button onClick={searchProducts} loading={productSearchLoading}>Search</Button>
                            </InlineStack>
                            {productResults.length > 0 && (
                                <BlockStack gap="150">
                                    {productResults.map((product) => {
                                        const alreadyAdded = group.products.some((item) => item.shopifyProductId === product.id && !item.shopifyProductVariantId);
                                        return (
                                            <InlineStack key={product.id} align="space-between" blockAlign="center">
                                                <InlineStack gap="200" blockAlign="center">
                                                    <Thumbnail source={product.featuredImage || ''} alt={product.title} size="small" />
                                                    <BlockStack gap="050">
                                                        <Text as="span" fontWeight="semibold">{product.title}</Text>
                                                        <Text as="span" tone="subdued">{product.sku || product.vendor || 'No SKU'}</Text>
                                                    </BlockStack>
                                                </InlineStack>
                                                <Button disabled={alreadyAdded} onClick={() => addProductToPackageItem(groupIndex, product)}>
                                                    {alreadyAdded ? 'Added' : 'Add'}
                                                </Button>
                                            </InlineStack>
                                        );
                                    })}
                                </BlockStack>
                            )}
                            {group.products.length > 0 && (
                                <BlockStack gap="150">
                                    {group.products.map((product, productIndex) => (
                                        <InlineStack key={`${product.shopifyProductId}-${productIndex}`} align="space-between" blockAlign="center">
                                            <InlineStack gap="200" blockAlign="center">
                                                <Thumbnail source={product.featuredImage || ''} alt={product.productTitle} size="small" />
                                                <BlockStack gap="050">
                                                    <Text as="span" fontWeight="semibold">{product.productTitle || product.shopifyProductId}</Text>
                                                    <Text as="span" tone="subdued">{product.sku || 'No SKU'}</Text>
                                                </BlockStack>
                                            </InlineStack>
                                            <Button tone="critical" onClick={() => removeProductFromPackageItem(groupIndex, productIndex)}>Remove</Button>
                                        </InlineStack>
                                    ))}
                                </BlockStack>
                            )}
                        </BlockStack>
                    </BlockStack>
                </div>
            );
        };

        return (
            <Card>
                <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="start">
                        <BlockStack gap="100">
                            <Text as="h2" variant="headingMd">{title}</Text>
                            <Text as="p" tone="subdued">Configure where products come from and which answer attributes filter each item.</Text>
                        </BlockStack>
                        <Button onClick={addPackageItem}>Add item</Button>
                    </InlineStack>

                    {guide.resultGroups.length === 0 && (
                        <Banner tone="info">Add a package item or product result so the guide has products to recommend.</Banner>
                    )}

                    {guide.resultGroups.length > 0 && (
                        <div style={questionListStyle}>
                            <div style={packageItemHeaderGridStyle}>
                                <div />
                                <Text as="span" variant="bodySm" fontWeight="medium">Item</Text>
                                <Text as="span" variant="bodySm" fontWeight="medium">Behavior</Text>
                                <Text as="span" variant="bodySm" fontWeight="medium">Required</Text>
                                <Text as="span" variant="bodySm" fontWeight="medium">Actions</Text>
                            </div>

                            {guide.resultGroups.map((group, groupIndex) => (
                                <Fragment key={`${group.id ?? 'group'}-${groupIndex}`}>
                                    {renderPackageItemRow(group, groupIndex, {
                                        isDragging: packageItemDrag?.sourceIndex === groupIndex,
                                        onHandleDragStart: packageItemDrag ? undefined : (event) => startPackageItemDrag(event, groupIndex),
                                        setRef: (node) => {
                                            if (node) {
                                                packageItemRowRefs.current.set(groupIndex, node);
                                            } else {
                                                packageItemRowRefs.current.delete(groupIndex);
                                            }
                                        },
                                    })}
                                    {expandedPackageItemKey === String(groupIndex) && renderPackageItemDetails(group, groupIndex)}
                                </Fragment>
                            ))}
                        </div>
                    )}

                    {packageItemDrag && draggedPackageItem ? createPortal((
                        <div
                            style={{
                                left: packageItemDrag.pointerX - packageItemDrag.offsetX,
                                pointerEvents: 'none',
                                position: 'fixed',
                                top: packageItemDrag.pointerY - packageItemDrag.offsetY,
                                width: packageItemDrag.width,
                                zIndex: 2147483647,
                            }}
                        >
                            {renderPackageItemRow(draggedPackageItem, packageItemDrag.sourceIndex, { isPreview: true })}
                        </div>
                    ), packageItemDrag.portalContainer) : null}

                </BlockStack>
            </Card>
        );
    };

    const renderRecommendedPackages = () => {
        if (!guide) return null;

        return (
            <Card>
                <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="start">
                        <BlockStack gap="100">
                            <Text as="h2" variant="headingMd">Recommended packages</Text>
                            <Text as="p" tone="subdued">Create optional exact product combinations that can be shown after questions are answered.</Text>
                        </BlockStack>
                        <Button onClick={() => openRecommendedPackageModal(null)}>Add package</Button>
                    </InlineStack>

                    {guide.recommendedPackages.length === 0 && (
                        <Banner tone="info">Recommended packages are optional. Use them when you want to offer hand-picked bundles.</Banner>
                    )}

                    <BlockStack gap="200">
                        {guide.recommendedPackages.map((pkg, packageIndex) => (
                            <Box key={`${pkg.id ?? 'package'}-${packageIndex}`} padding="300" background="bg-surface-secondary" borderRadius="200">
                                <InlineStack align="space-between" blockAlign="center">
                                    <BlockStack gap="050">
                                        <InlineStack gap="200" blockAlign="center">
                                            <Text as="span" fontWeight="semibold">{pkg.name || 'Untitled package'}</Text>
                                            {!pkg.isEnabled && <Badge tone="warning">Disabled</Badge>}
                                        </InlineStack>
                                        <Text as="p" tone="subdued">{pkg.items.length} item{pkg.items.length === 1 ? '' : 's'}</Text>
                                    </BlockStack>
                                    <InlineStack gap="200">
                                        <Button onClick={() => openRecommendedPackageModal(packageIndex)}>Edit</Button>
                                        <Button tone="critical" onClick={() => removeRecommendedPackage(packageIndex)}>Remove</Button>
                                    </InlineStack>
                                </InlineStack>
                            </Box>
                        ))}
                    </BlockStack>

                </BlockStack>
            </Card>
        );
    };

    const renderDiscounts = () => {
        if (!guide) return null;

        return (
            <Card>
                <BlockStack gap="400">
                    <BlockStack gap="100">
                        <Text as="h2" variant="headingMd">Discount & cart settings</Text>
                        <Text as="p" tone="subdued">Control bundle discounts and checkout behavior for selected package items.</Text>
                    </BlockStack>
                    <FormLayout>
                        <Checkbox
                            label="Enable bundle discount"
                            checked={guide.discountEnabled}
                            onChange={(checked) => patchGuide({ discountEnabled: checked })}
                        />
                        <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
                            <Select
                                label="Discount type"
                                options={discountTypeOptions}
                                value={guide.discountType}
                                disabled={!guide.discountEnabled}
                                onChange={(value) => patchGuide({ discountType: value })}
                            />
                            <TextField
                                label={guide.discountType === 'FixedAmount' ? 'Fixed amount' : 'Percentage'}
                                type="number"
                                value={numberValue(guide.discountType === 'FixedAmount' ? guide.discountFixedAmount : guide.discountPercentage)}
                                disabled={!guide.discountEnabled}
                                suffix={guide.discountType === 'FixedAmount' ? undefined : '%'}
                                prefix={guide.discountType === 'FixedAmount' ? '$' : undefined}
                                onChange={(value) => {
                                    if (guide.discountType === 'FixedAmount') {
                                        patchGuide({ discountFixedAmount: parseNullableDecimal(value) });
                                    } else {
                                        patchGuide({ discountPercentage: parseNullableDecimal(value) });
                                    }
                                }}
                                autoComplete="off"
                            />
                            <TextField
                                label="Minimum items"
                                type="number"
                                value={numberValue(guide.discountMinimumItems)}
                                disabled={!guide.discountEnabled}
                                onChange={(value) => patchGuide({ discountMinimumItems: parseNullablePositiveNumber(value) })}
                                autoComplete="off"
                            />
                        </InlineGrid>
                    </FormLayout>
                </BlockStack>
            </Card>
        );
    };

    const renderPreview = () => {
        if (!guide) return null;

        return (
            <Card>
                <BlockStack gap="400">
                    <BlockStack gap="100">
                        <Text as="h2" variant="headingMd">Preview</Text>
                        <Text as="p" tone="subdued">A customer-facing outline of the guide flow. Product availability is resolved on the storefront.</Text>
                    </BlockStack>
                    <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                        <BlockStack gap="400">
                            <Text as="h3" variant="headingLg">{guide.name || 'Product guide'}</Text>
                            {guide.description && <Text as="p" tone="subdued">{guide.description}</Text>}
                            {guide.questions.map((question, index) => (
                                <Box key={`${question.id ?? 'preview-question'}-${index}`} padding="300" background="bg-surface" borderRadius="200">
                                    <BlockStack gap="300">
                                        <Text as="h4" variant="headingMd">{index + 1}. {question.prompt || 'Untitled question'}</Text>
                                        <InlineStack gap="200">
                                            {question.answers.filter((answer) => answer.isEnabled).map((answer) => (
                                                <Button key={`${answer.id ?? answer.valueKey}-${answer.displayOrder}`}>{answer.label || 'Answer'}</Button>
                                            ))}
                                        </InlineStack>
                                    </BlockStack>
                                </Box>
                            ))}
                            <Box padding="300" background="bg-surface" borderRadius="200">
                                <BlockStack gap="300">
                                    <Text as="h4" variant="headingMd">Final review</Text>
                                    <BlockStack gap="150">
                                        {guide.resultGroups.map((group) => (
                                            <InlineStack key={group.slotKey || group.title} align="space-between">
                                                <Text as="span">{group.title || 'Package item'}</Text>
                                                <Text as="span" tone="subdued">{group.isRequired ? 'Required' : 'Optional'}</Text>
                                            </InlineStack>
                                        ))}
                                    </BlockStack>
                                </BlockStack>
                            </Box>
                        </BlockStack>
                    </Box>
                </BlockStack>
            </Card>
        );
    };

    const renderActiveSection = () => {
        switch (activeSection) {
            case 'questions':
                return renderQuestions();
            case 'items':
                return renderPackageItems();
            case 'packages':
                return renderRecommendedPackages();
            case 'discounts':
                return renderDiscounts();
            case 'preview':
                return renderPreview();
            default:
                return renderGuideHome();
        }
    };

    const renderAnswerModal = () => {
        if (!answerDraft || !guide) return null;
        const question = guide.questions[answerDraft.questionIndex];
        const attribute = getQuestionAttribute(question);
        const selectedValues = answerDraft.answer.matchingValues.map((value) => value.normalizedValue || value.value);

        const matchingChoices = (attribute?.values ?? []).map((value) => ({
            label: `${value.value} (${value.productCount + value.variantCount})`,
            value: value.normalizedValue || value.value,
        }));

        const updateAnswer = (changes: Partial<ProductGuideAnswer>) => {
            setAnswerDraft((current) => current ? { ...current, answer: { ...current.answer, ...changes } } : current);
        };

        const mapMatchingValues = (selected: string[]): ProductGuideAnswerMatchingValue[] => {
            const valuesByKey = new Map<string, ShopifyMetafieldAttributeValueOption>();
            (attribute?.values ?? []).forEach((value) => valuesByKey.set(value.normalizedValue || value.value, value));
            return selected.map((key) => {
                const value = valuesByKey.get(key);
                return {
                    value: value?.value ?? key,
                    normalizedValue: value?.normalizedValue ?? key,
                };
            });
        };

        return (
            <Modal
                open
                onClose={() => setAnswerDraft(undefined)}
                title={answerDraft.answerIndex === null ? 'Add answer' : 'Edit answer'}
                primaryAction={{ content: 'Save answer', onAction: saveAnswerDraft }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setAnswerDraft(undefined) }]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        <FormLayout>
                            <TextField
                                label="Label"
                                value={answerDraft.answer.label}
                                onChange={(value) => updateAnswer({ label: value, valueKey: answerDraft.answer.valueKey || toKey(value, 'answer') })}
                                autoComplete="off"
                            />
                            <TextField
                                label="Description"
                                value={answerDraft.answer.helpText}
                                onChange={(value) => updateAnswer({ helpText: value })}
                                multiline={2}
                                autoComplete="off"
                            />
                            <TextField
                                label="Image URL"
                                value={answerDraft.answer.imageUrl}
                                onChange={(value) => updateAnswer({ imageUrl: value })}
                                autoComplete="off"
                            />
                            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                                <Checkbox
                                    label="Enabled"
                                    checked={answerDraft.answer.isEnabled}
                                    onChange={(checked) => updateAnswer({ isEnabled: checked })}
                                />
                                <Checkbox
                                    label="Default answer"
                                    checked={answerDraft.answer.isDefault}
                                    onChange={(checked) => updateAnswer({ isDefault: checked })}
                                />
                            </InlineGrid>
                        </FormLayout>

                        <Divider />
                        <BlockStack gap="200">
                            <Text as="h3" variant="headingSm">Matching values</Text>
                            {attribute ? (
                                <ChoiceList
                                    title={`Values from ${attribute.name}`}
                                    allowMultiple
                                    choices={matchingChoices}
                                    selected={selectedValues}
                                    onChange={(selected) => updateAnswer({ matchingValues: mapMatchingValues(selected) })}
                                />
                            ) : (
                                <Banner tone="info">Connect this question to an attribute before mapping answer values.</Banner>
                            )}
                        </BlockStack>
                    </BlockStack>
                </Modal.Section>
            </Modal>
        );
    };

    const renderRecommendedPackageModal = () => {
        if (!recommendedPackageDraft || !guide) return null;
        const pkg = recommendedPackageDraft.package;
        const savedGroups = guide.resultGroups.filter((group) => !!group.id);
        const groupOptions = [
            { label: 'Choose item', value: '' },
            ...savedGroups.map((group) => ({ label: group.title || group.slotKey, value: String(group.id) })),
        ];

        const updatePackage = (changes: Partial<ProductGuideRecommendedPackage>) => {
            setRecommendedPackageDraft((current) => current ? { ...current, package: { ...current.package, ...changes } } : current);
        };

        const updateItem = (itemIndex: number, changes: Partial<ProductGuideRecommendedPackageItem>) => {
            updatePackage({
                items: pkg.items.map((item, index) => (index === itemIndex ? { ...item, ...changes } : item)),
            });
        };

        const addItem = () => updatePackage({ items: toOrdered([...pkg.items, createRecommendedPackageItem(pkg.items.length + 1)]) });
        const removeItem = (itemIndex: number) => updatePackage({ items: toOrdered(pkg.items.filter((_, index) => index !== itemIndex)) });

        return (
            <Modal
                open
                onClose={() => setRecommendedPackageDraft(undefined)}
                title={recommendedPackageDraft.packageIndex === null ? 'Add recommended package' : 'Edit recommended package'}
                primaryAction={{ content: 'Save package', onAction: saveRecommendedPackageDraft }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setRecommendedPackageDraft(undefined) }]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        <FormLayout>
                            <TextField label="Name" value={pkg.name} onChange={(value) => updatePackage({ name: value })} autoComplete="off" />
                            <TextField label="Description" value={pkg.description} onChange={(value) => updatePackage({ description: value })} multiline={2} autoComplete="off" />
                            <TextField label="Image URL" value={pkg.imageUrl} onChange={(value) => updatePackage({ imageUrl: value })} autoComplete="off" />
                            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                                <Checkbox label="Enabled" checked={pkg.isEnabled} onChange={(checked) => updatePackage({ isEnabled: checked })} />
                                <Checkbox label="Eligible for bundle discount" checked={pkg.discountEligible} onChange={(checked) => updatePackage({ discountEligible: checked })} />
                            </InlineGrid>
                        </FormLayout>
                        <Divider />
                        <InlineStack align="space-between" blockAlign="center">
                            <Text as="h3" variant="headingSm">Package products</Text>
                            <Button disabled={savedGroups.length === 0} onClick={addItem}>Add product</Button>
                        </InlineStack>
                        {savedGroups.length === 0 && (
                            <Banner tone="info">Save at least one package item before adding products to a recommended package.</Banner>
                        )}
                        <BlockStack gap="300">
                            {pkg.items.map((item, itemIndex) => {
                                const selectedGroup = savedGroups.find((group) => String(group.id) === String(item.productGuideResultGroupId || ''));
                                const productOptions = [
                                    { label: 'Manual product', value: '' },
                                    ...(selectedGroup?.products ?? []).map((product) => ({
                                        label: `${product.productTitle}${product.variantTitle ? ` / ${product.variantTitle}` : ''}`,
                                        value: String(product.shopifyProductId),
                                    })),
                                ];

                                return (
                                    <Box key={`${item.id ?? 'item'}-${itemIndex}`} padding="300" background="bg-surface-secondary" borderRadius="200">
                                        <BlockStack gap="300">
                                            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                                                <Select
                                                    label="Package item"
                                                    options={groupOptions}
                                                    value={String(item.productGuideResultGroupId || '')}
                                                    onChange={(value) => {
                                                        const group = savedGroups.find((candidate) => String(candidate.id) === value);
                                                        updateItem(itemIndex, {
                                                            productGuideResultGroupId: group?.id ?? 0,
                                                            productGuideResultGroupTitle: group?.title ?? '',
                                                        });
                                                    }}
                                                />
                                                <Select
                                                    label="Product"
                                                    options={productOptions}
                                                    value={String(item.shopifyProductId || '')}
                                                    onChange={(value) => {
                                                        const product = selectedGroup?.products.find((candidate) => String(candidate.shopifyProductId) === value);
                                                        updateItem(itemIndex, {
                                                            shopifyProductId: product?.shopifyProductId ?? 0,
                                                            shopifyProductVariantId: product?.shopifyProductVariantId ?? null,
                                                            productTitle: product?.productTitle ?? '',
                                                            variantTitle: product?.variantTitle ?? '',
                                                            sku: product?.sku ?? '',
                                                            featuredImage: product?.featuredImage ?? '',
                                                        });
                                                    }}
                                                />
                                            </InlineGrid>
                                            <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
                                                <TextField label="Manual product ID" type="number" value={item.shopifyProductId ? String(item.shopifyProductId) : ''} onChange={(value) => updateItem(itemIndex, { shopifyProductId: parsePositiveNumber(value, 0) })} autoComplete="off" />
                                                <TextField label="Manual variant ID" type="number" value={item.shopifyProductVariantId ? String(item.shopifyProductVariantId) : ''} onChange={(value) => updateItem(itemIndex, { shopifyProductVariantId: parseNullablePositiveNumber(value) })} autoComplete="off" />
                                                <TextField label="Quantity" type="number" value={numberValue(item.quantity)} onChange={(value) => updateItem(itemIndex, { quantity: parsePositiveNumber(value) })} autoComplete="off" />
                                            </InlineGrid>
                                            <TextField label="Product title" value={item.productTitle} onChange={(value) => updateItem(itemIndex, { productTitle: value })} autoComplete="off" />
                                            <InlineStack align="end">
                                                <Button tone="critical" onClick={() => removeItem(itemIndex)}>Remove product</Button>
                                            </InlineStack>
                                        </BlockStack>
                                    </Box>
                                );
                            })}
                        </BlockStack>
                    </BlockStack>
                </Modal.Section>
            </Modal>
        );
    };

    if (loadingInitial || !guide) {
        return (
            <SkeletonPage primaryAction>
                <Layout>
                    <Layout.Section>
                        <Card>
                            <SkeletonBodyText lines={8} />
                        </Card>
                    </Layout.Section>
                </Layout>
            </SkeletonPage>
        );
    }

    return (
        <Page
            title={guide.id ? guide.name || 'Product guide' : 'Create product guide'}
            backAction={{ content: 'Product Guides', onAction: () => navigate('/product-guides') }}
            primaryAction={{ content: 'Save', loading: loadingSave, onAction: handleSave }}
            secondaryActions={guide.id ? [{ content: 'Delete', destructive: true, onAction: handleDelete }] : []}
        >
            <BlockStack gap="400">
                {setupDataError && <Banner tone="critical">{setupDataError}</Banner>}
                {saveError && <Banner tone="critical">{saveError}</Banner>}
                {renderSectionTabs()}
                {renderActiveSection()}
            </BlockStack>
            {renderAnswerModal()}
            {renderRecommendedPackageModal()}
        </Page>
    );
};

export default observer(ProductGuideEditorPage);
